/**
 * What version is in your hand, and is there a newer one.
 *
 * Two kinds of update, checked together:
 *
 * - Native. A new build in the App Store or Play Store. The app compares its
 *   own build number to the newest active row for its platform in
 *   `app_releases` and, if behind, sends the person to the store page.
 *
 * - Over the air. The web bundle (this React app, built and zipped) served
 *   from our own storage. A bundle row carries the oldest native build it
 *   still works with on each platform, because a bundle that calls a plugin
 *   the installed shell does not have would break the app. When one applies,
 *   the plugin downloads it, verifies it, swaps it in and reloads.
 *
 * The version a person sees is the native version and build, because that is
 * the thing they installed. The bundle stamp sits beside it so support can
 * tell which web build they are running after an OTA.
 *
 * Nothing here decides on its own to update. The Support screen asks, shows
 * what it found, and the person taps.
 */

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { Browser } from '@capacitor/browser';
import { supabase } from '../lib/supabase';

// Stamped at build time by scripts/build-web.sh, which is what `npm run build`
// runs. A build made by calling react-scripts directly has neither, so it
// reports version 0.0.0 and stamp 0 - the state that made a 4.3.4 bundle look
// newer than the 4.3.5 shell it was sitting under. The build script fails
// loudly rather than shipping one of those again.
export const BUNDLE_VERSION = process.env.REACT_APP_VERSION || '0.0.0';
export const BUNDLE_STAMP = Number(process.env.REACT_APP_BUILD_STAMP || 0);
// Which OTA this is, counting from the first one ever published. The stamp
// above is a timestamp: correct, sortable, and unreadable out loud. This is
// the number to say on the phone and to match against a screenshot. Stamped
// by scripts/release-ota.sh; a built-in bundle has none and reports 0.
export const OTA_NUMBER = Number(process.env.REACT_APP_OTA_NUMBER || 0);

// Imported statically on purpose. A dynamic import() of the plugin makes
// webpack fetch a separate chunk, and that load failed silently on the phone,
// which left the version line waiting for ever. registerPlugin is a proxy; on
// the web its calls reject, which is handled.
function updater() {
  return Capacitor.isNativePlatform() ? CapacitorUpdater : null;
}

/**
 * A native call that cannot hang the screen. Anything the shell has not
 * answered in this long counts as unavailable and the caller falls back.
 */
function within(ms, promise) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`timed out after ${ms}ms`)), ms)),
  ]);
}
const NATIVE_MS = 4000;

/**
 * Tell the updater the bundle came up. Without this a freshly applied OTA
 * bundle is rolled back on the next launch as a failed update. Call once, as
 * early as the app renders.
 */
export async function notifyAppReady() {
  const u = updater();
  if (u) await u.notifyAppReady().catch(() => {});
}
export const OTA_ENABLED = true;

/**
 * @returns {Promise<{platform:'ios'|'android'|'web', version:string, build:number, bundleStamp:number, bundleVersion:string, otaNumber:number, otaBundle:string|null}>}
 */
export async function getInstalled() {
  const platform = Capacitor.getPlatform();
  let version = BUNDLE_VERSION;
  let build = 0;
  if (Capacitor.isNativePlatform()) {
    try {
      const info = await within(NATIVE_MS, App.getInfo());
      version = info.version || version;
      build = Number(info.build) || 0;
    } catch (err) {
      console.warn('[update] App.getInfo unavailable:', err?.message);
    }
  }
  let otaBundle = null;
  const u = updater();
  if (u) {
    try {
      const cur = await within(NATIVE_MS, u.current());
      const id = cur?.bundle?.id;
      otaBundle = id && id !== 'builtin' ? (cur.bundle.version || id) : null;
      if (!build && cur?.native) version = cur.native;
    } catch (err) {
      console.warn('[update] CapacitorUpdater.current unavailable:', err?.message);
    }
  }
  return {
    platform, version, build,
    bundleStamp: BUNDLE_STAMP, bundleVersion: BUNDLE_VERSION,
    otaNumber: OTA_NUMBER, otaBundle,
  };
}

/**
 * Compare two dotted version strings. -1, 0 or 1, the way a sort comparator
 * reads: `compareVersions('4.3.5', '4.3.4') === 1`.
 *
 * Numeric and segment by segment, missing segments count as zero so "4.3" and
 * "4.3.0" are the same release, and a trailing suffix is ignored rather than
 * poisoning the whole comparison ("4.3.5-rc1" compares as 4.3.5). Exported so
 * the gate below can be tested without a phone.
 */
export function compareVersions(a, b) {
  const parts = (v) => String(v == null ? '' : v).split('.').map((seg) => parseInt(seg, 10) || 0);
  const x = parts(a);
  const y = parts(b);
  for (let i = 0; i < Math.max(x.length, y.length); i += 1) {
    const d = (x[i] || 0) - (y[i] || 0);
    if (d !== 0) return d > 0 ? 1 : -1;
  }
  return 0;
}

/**
 * The version of the web bundle actually running right now.
 *
 * The native shell's version is the floor and always counts: the shell ships
 * with its own web bundle inside it, so a bundle older than the installed shell
 * is never a step forward, whatever a row says. When an OTA has been applied
 * the running bundle is that one instead, and it can be ahead of the shell, so
 * take whichever of the two is greater.
 *
 * BUNDLE_VERSION falls back to '0.0.0' when a build was made without the stamp
 * (see the build script). That fallback must never drag the floor below the
 * native version, which is why this takes the greater rather than preferring
 * the bundle.
 *
 * Note what this deliberately is NOT: a report of which code is running. A
 * 4.3.5 bundle inside a 4.3.7 shell answers 4.3.7 here, because the question
 * being asked is "what is the lowest version this device counts as having" -
 * and the shell's own bundle is always available to it. Anything that wants to
 * SHOW what is running reads `formatRunning`, which says the bundle.
 */
export function runningBundleVersion(installed) {
  const native = installed?.version || '0.0.0';
  if (!installed?.otaBundle) return native;
  const bundle = installed?.bundleVersion || BUNDLE_VERSION;
  return compareVersions(bundle, native) > 0 ? bundle : native;
}

/** A bundle stamp as a date: 202609091317 -> "09.09.2026 1:17 PM". */
export function formatStamp(stamp) {
  const t = String(stamp || '');
  if (t.length !== 12) return '';
  const h = Number(t.slice(8, 10)); const m = t.slice(10, 12);
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${t.slice(4, 6)}.${t.slice(6, 8)}.${t.slice(0, 4)} ${hour12}:${m} ${h < 12 ? 'AM' : 'PM'}`;
}

/**
 * The same stamp, short enough to sit in brackets where a native build number
 * would: 202609111922 -> "09.11 7:22 PM".
 *
 * A bundle has no build number. It has a twelve digit minute stamp, which is
 * monotonic and therefore correct, and unreadable. Several bundles ship in a
 * day and they all carry the same version string, so the thing that tells two
 * of them apart is the time - drop the year and it fits beside a native (83)
 * without anyone having to decode it.
 */
export function formatStampShort(stamp) {
  const t = String(stamp || '');
  if (t.length !== 12) return '';
  const h = Number(t.slice(8, 10));
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${t.slice(4, 6)}.${t.slice(6, 8)} ${hour12}:${t.slice(10, 12)} ${h < 12 ? 'AM' : 'PM'}`;
}

/**
 * What a release is, before it is taken. Version, build, and which kind:
 * "4.3.4 (83) Native" goes to the store or the APK, "4.3.5 (100) OTA 64 ·
 * 09.16 11:44 AM" installs in place.
 *
 * Native and OTA are jargon. They are the right words for the people reading
 * this today, who are testing builds and need to know which pipe an update came
 * down; if this app ever reaches people who are only here for the cards, this
 * line is the first thing to rewrite.
 *
 * @param {'native'|'ota'} kind
 * @param {{version:string, build:number}|null} row
 */
export function formatRelease(kind, row, nativeBuild = 0) {
  if (!row) return '';
  // The brackets always hold a native build number, on every line, because that
  // is the number TestFlight and Play list and the one to quote in a report.
  //
  // An OTA has no build number of its own - it swaps the web bundle and leaves
  // the shell alone - so the brackets hold the build it will be running on,
  // which is the one already installed. Its own stamp follows the word OTA.
  // The owner asked for the build number "every single time", and an OTA line
  // that showed only a time was the case that dropped it.
  if (kind === 'ota') {
    const on = nativeBuild ? ` (${nativeBuild})` : '';
    const when = formatStampShort(row.build);
    // An OTA is named by its own counting number when it has one, which is
    // the thing to say out loud. Rows published before numbering fall back to
    // the time, which is all they ever had.
    const which = row.ota_number ? `OTA ${row.ota_number}` : 'OTA';
    return `${row.version}${on} ${which}${when ? ` · ${when}` : ''}`;
  }
  return `${row.version}${row.build ? ` (${row.build})` : ''} Native`;
}

/**
 * Was this bundle published after the native build in somebody's hand?
 *
 * Installing the app from TestFlight or Play gets you a shell with a web bundle
 * baked into it. An OTA published before that shell was cut is older code than
 * you already have; one published after is newer. Both carry the same version
 * string for days at a time, so the version alone cannot tell them apart and
 * the question "am I actually up to date" has no visible answer.
 *
 * Publish time answers it with no new column: every release already records
 * `published_at`, native and OTA alike.
 *
 * @returns {true|false|null} null when either side is unknown.
 */
export function publishedAfterNative(otaRow, nativeRow) {
  const a = otaRow?.published_at;
  const b = nativeRow?.published_at;
  if (!a || !b) return null;
  const at = Date.parse(a);
  const bt = Date.parse(b);
  if (Number.isNaN(at) || Number.isNaN(bt)) return null;
  return at > bt;
}

/**
 * That relationship as a sentence, or '' when it cannot be told.
 *
 * Said plainly and from the reader's side: their build, their app.
 */
export function nativeRelationLine(otaRow, nativeRow) {
  const after = publishedAfterNative(otaRow, nativeRow);
  if (after === null) return '';
  const build = nativeRow?.build;
  const on = build ? ` build ${build}` : ' build';
  return after
    ? `Published after your${on}.`
    : `Published before your${on}, so your app is already newer.`;
}

/**
 * What is running, in one line: the native build in brackets, then how the
 * code in it arrived. "4.3.5 (98) · OTA 64 09.14 6:47 AM" or
 * "4.3.5 (99) · built-in".
 *
 * The owner asked where the build numbers went. The OTA time had taken the
 * brackets, which left the native build - the thing TestFlight and Play list -
 * nowhere on the screen.
 */
export function formatRunning(installed, runningVersion, stampShort) {
  if (!installed) return '';
  const build = installed.build ? ` (${installed.build})` : '';
  // The bundle carries its own OTA number, so this line can name it without
  // waiting on the network. A built-in bundle has none and says so.
  const n = installed.otaBundle ? installed.otaNumber : 0;
  const kind = installed.otaBundle ? (n ? `OTA ${n}` : 'OTA') : 'built-in';
  return `${runningVersion}${build} · ${kind}${stampShort ? ` ${stampShort}` : ''}`;
}

const PENDING_KEY = 'cc:update:pending';

/**
 * Remember which bundle is being installed, so the next boot can say so.
 * The install reloads the whole app, and without this the person lands on
 * a screen with no sign anything happened.
 */
export function rememberPendingInstall(row) {
  try { localStorage.setItem(PENDING_KEY, JSON.stringify({ build: row.build, version: row.version, at: Date.now() })); } catch { /* fine */ }
}

/**
 * The install that just completed, if this bundle is the one that was
 * remembered. Cleared on read. Null when this boot is not the result of an
 * install, or the remembered bundle never arrived (rolled back).
 */
export function takeCompletedInstall() {
  let pending = null;
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (raw) { pending = JSON.parse(raw); localStorage.removeItem(PENDING_KEY); }
  } catch { return null; }
  if (!pending) return null;
  return Number(pending.build) === BUNDLE_STAMP ? pending : null;
}

/** "4.3.1 (68)" for the bottom of a screen. */
export function formatInstalled(installed) {
  if (!installed) return '';
  const base = installed.build ? `${installed.version} (${installed.build})` : installed.version;
  return base;
}

function newest(rows, channel) {
  return rows.filter((r) => r.channel === channel).sort((a, b) => b.build - a.build)[0] || null;
}

/**
 * Is this bundle an update to what is running, or the thing already running?
 *
 * Pure, and separate from the read, because the whole bug lived in this
 * decision and a decision that cannot be tested without a network is a
 * decision nobody tests.
 *
 * Three questions, in order, and the first two can only say no:
 *
 * 1. Can this shell run it at all - `min_native`.
 * 2. Is it the bundle already running. A stamp names exactly one bundle, so
 *    this is identity and it outranks everything below. Without it the Support
 *    screen offered the running bundle whenever the version bookkeeping
 *    disagreed with the stamp, and installing it changed nothing, so the offer
 *    came straight back: "the OTA keeps popping out no matter which version
 *    I'm on".
 * 3. Is it newer. Version dominates, because a stamp is wall-clock time and an
 *    older release's bundle can carry a later stamp than a newer release's.
 *    Same version, newer stamp is the everyday case of several bundles in a
 *    day.
 *
 * @param {{platform:string, build:number, version:string, bundleStamp:number,
 *          bundleVersion:string, otaBundle:string|null}} installed
 * @param {{version:string, build:number, min_native:object}} ota
 */
export function otaIsAnUpdate(installed, ota) {
  if (!installed || !ota) return false;
  const minBuild = Number(ota.min_native?.[installed.platform] || 0);
  if (!(installed.build >= minBuild)) return false;

  const stamp = Number(installed.bundleStamp) || 0;
  if (stamp > 0 && Number(ota.build) === stamp) return false;

  const cmp = compareVersions(ota.version, runningBundleVersion(installed));
  return cmp > 0 || (cmp === 0 && Number(ota.build) > stamp);
}

/**
 * Ask the registry. Never throws; a failed read reports nothing available
 * with `error` set so the screen can say "could not check".
 *
 * @returns {Promise<{installed, native:{available:boolean,row:object|null}, ota:{available:boolean,row:object|null}, error:string|null}>}
 */
export async function checkForUpdate() {
  const installed = await getInstalled();
  const result = {
    installed,
    native: { available: false, row: null },
    ota: { available: false, row: null },
    installedNative: null,
    running: null,
    error: null,
  };

  let data; let error;
  try {
    ({ data, error } = await within(12000, supabase
      .from('app_releases')
      .select('id,channel,version,build,ota_number,bundle_url,checksum,min_native,store_url,apk_url,notes,published_at')
      .eq('active', true)));
  } catch (err) { error = err; }
  if (error) { result.error = error.message || String(error); return result; }
  const rows = data || [];

  // The row for what is ALREADY running, so a screen can show the notes for the
  // version in somebody's hand rather than only for the one on offer. An OTA
  // bundle is matched on its stamp; with no OTA applied the running web bundle
  // is the one that shipped inside the native build, so the native row is the
  // honest answer. Null when nothing matches, which is normal for a build made
  // before a row existed for it.
  const runningRow = installed.bundleStamp
    ? rows.find((r) => r.channel === 'ota' && r.build === installed.bundleStamp) || null
    : null;

  // A bundle built before OTA numbering existed does not carry its own number,
  // but the registry knows it. Take it from the row so an older bundle still
  // names itself once the check has run.
  if (!installed.otaNumber && runningRow?.ota_number) {
    installed.otaNumber = runningRow.ota_number;
  }

  if (installed.platform === 'ios' || installed.platform === 'android') {
    const row = newest(rows, installed.platform);
    result.running = runningRow
      || rows.find((r) => r.channel === installed.platform && r.build === installed.build)
      || null;
    result.native.row = row;
    result.native.available = Boolean(row && installed.build && row.build > installed.build);

    // The release row for the build actually installed, so a screen can say
    // whether a bundle predates it. Distinct from `running`, which is about
    // which notes to show.
    result.installedNative = rows.find(
      (r) => r.channel === installed.platform && r.build === installed.build,
    ) || null;

    const ota = newest(rows, 'ota');
    if (ota && ota.bundle_url) {
      // Version first, stamp only to break a tie.
      //
      // This used to be the stamp alone, and it handed out a backwards update:
      // a fresh install of native 4.3.5 (84) was offered a 4.3.4 bundle stamped
      // later in the evening than 4.3.5 was built. Stamps are wall clock time,
      // so an older release's bundle can carry a newer stamp than a newer
      // release's, and the 4.3.5 shell had been built without a stamp at all,
      // which left it comparing against 0 and losing to everything on the table.
      //
      // A bundle is an update only when its (version, stamp) pair is strictly
      // greater than what is running, with the version dominant. Older version:
      // never, whatever the stamps say. Newer version: yes, min_native still
      // decides whether this shell can run it. Same version: the newer stamp
      // wins, which is the everyday case of several bundles in one day.
      result.ota.row = ota;
      result.ota.available = OTA_ENABLED && otaIsAnUpdate(installed, ota);
    }
  }
  return result;
}

/**
 * ---------------------------------------------------------------------------
 * The decision layer: is there something to offer, and has this person already
 * said no to it.
 *
 * Deliberately separate from the mechanism that performs an update. Whether to
 * prompt, which version, which kind, and what has been skipped is ours forever.
 * How the update is actually taken is per platform and will change: the store
 * link today, Play's In-App Update API on Android once the app is distributed
 * through Play properly (it can present its own prompt and download in the
 * background, with no app_releases row to maintain), the direct APK for
 * sideloaded installs, the bundle in place for OTA. Apple has no equivalent, so
 * on iOS the banner below stays the answer.
 *
 * The seam is `nativeUpdateActions` further down. A new mechanism is one more
 * entry in that list and no screen changes.
 * ---------------------------------------------------------------------------
 */

const SKIP_KEY = 'cc:update:skipped';

function readSkipped() {
  try {
    const v = JSON.parse(localStorage.getItem(SKIP_KEY) || '{}');
    return { native: Number(v?.native) || 0, ota: Number(v?.ota) || 0 };
  } catch { return { native: 0, ota: 0 }; }
}

/** The highest build skipped, per kind. `{ native, ota }`, 0 for never skipped. */
export function getSkippedBuilds() {
  return readSkipped();
}

/**
 * "Not now." Remembered on the device so the next launch honours it, and only
 * for this build: a newer one asks again.
 *
 * The two kinds are remembered apart. Skipping a bundle must not silence a
 * native release, and skipping a store release must not silence a fix that can
 * be had over the air right now.
 *
 * @param {'native'|'ota'} kind
 * @param {number} build native build number, or an OTA stamp
 */
export function skipRelease(kind, build) {
  const b = Number(build) || 0;
  if (!b || (kind !== 'native' && kind !== 'ota')) return;
  const next = readSkipped();
  if (b <= next[kind]) return;
  next[kind] = b;
  try { localStorage.setItem(SKIP_KEY, JSON.stringify(next)); } catch { /* fine */ }
}

/** @param {'native'|'ota'} kind */
export function isSkipped(kind, build) {
  const b = Number(build) || 0;
  return b > 0 && b <= readSkipped()[kind];
}

/**
 * What this device should be offered right now: the registry's answer with
 * anything already skipped removed. Rows, not booleans, so the caller can name
 * the version. Never throws.
 *
 * Used by the launch banner. The Support screen asks `checkForUpdate` directly,
 * because someone who taps "Check for update" is asking to see everything,
 * including a build they skipped.
 *
 * @returns {Promise<{installed, native:object|null, ota:object|null, error:string|null}>}
 */
export async function updatesToOffer() {
  const result = await checkForUpdate();
  return {
    installed: result.installed,
    native: result.native.available && !isSkipped('native', result.native.row?.build) ? result.native.row : null,
    ota: result.ota.available && !isSkipped('ota', result.ota.row?.build) ? result.ota.row : null,
    // Carried through so the banner can say the same thing the Support screen
    // does about where a bundle sits relative to the installed shell.
    installedNative: result.installedNative,
    error: result.error,
  };
}

/**
 * The ways a native update can be taken on this device, in the order to offer
 * them. The seam: screens render whatever comes back and never decide for
 * themselves that "native" means "open the store".
 *
 * Where Play's In-App Update API goes: an `{ id: 'play' }` entry at the front
 * on Android, whose `run` hands off to the plugin instead of opening a URL. The
 * store link stays behind it for installs that did not come from Play.
 *
 * @returns {Array<{id:string, label:string, run:() => Promise<any>}>}
 */
export function nativeUpdateActions(installed, row) {
  const actions = [];
  const storeName = installed?.platform === 'ios' ? 'App Store' : 'Play Store';
  if (row?.store_url) actions.push({ id: 'store', label: `Open the ${storeName}`, run: () => openStore(row) });
  if (installed?.platform === 'android' && row?.apk_url) {
    actions.push({ id: 'apk', label: 'Download APK', run: () => openApk(row) });
  }
  return actions;
}

/**
 * Download the bundle and switch to it. The plugin reloads the app on
 * success, so this only returns on failure.
 */
export async function applyOta(row) {
  const u = updater();
  if (!u) throw new Error('Updates are only for the installed app.');
  rememberPendingInstall(row);
  const bundle = await u.download({
    url: row.bundle_url,
    version: String(row.build),
    checksum: row.checksum || undefined,
  });
  await u.set({ id: bundle.id });
}

/** Open the store page for a native update. */
export async function openStore(row) {
  return openLink(row?.store_url);
}

/**
 * Open the direct APK for a native update. For Android installs that did not
 * come from Play, where the store page cannot update them.
 */
export async function openApk(row) {
  const url = row?.apk_url;
  if (!url) return undefined;
  // Deliberately NOT the in-app browser the other links use.
  //
  // Browser.open puts the page in a Custom Tab, which is a browser living
  // inside this app. A download that lands there has to be handed back out to
  // the system installer, and that handoff is where an APK quietly fails. The
  // real browser and its download manager do this every day.
  //
  // The app is served from https://localhost and there is no allowNavigation
  // list, so app.cardchase.org is an external host: navigating to it is
  // intercepted and opened in the system browser rather than loading here. The
  // app does not go anywhere.
  if (Capacitor.getPlatform() === 'android') {
    try {
      window.location.href = url;
      return undefined;
    } catch {
      /* fall through to the in-app browser rather than leaving them stuck */
    }
  }
  return openLink(url);
}

async function openLink(url) {
  if (!url) return;
  try {
    await Browser.open({ url });
  } catch {
    window.open(url, '_blank');
  }
}

